import React from 'react';
import { browserHistory } from 'react-router';
import * as _ from 'lodash';
import { Checkbox, Icon, Table } from 'semantic-ui-react';
import { IntlShape } from 'react-intl';

import DisplayDate from 'app/components/DisplayDate';
import AgeFromDate from 'app/components/AgeFromDate';
import PatternUnstructuredList from 'app/components/PatternUnstructuredList';
import PACSSyncStatus from 'app/containers/MonitoringPage/PACSSyncStatus';
import TeleradiologyStudyShortStatus from 'app/containers/Teleradiology/StudyShortStatus';
import { predictionsMerger } from 'app/utils/predictions/utils';
import { Study } from 'app/interfaces/Study';
import {
  ABDOMEN_PATTERNS,
  THORAX_PATTERNS,
} from 'app/components/PatternStructuredList/patternsGroups';
import VHSInlineDisplay from 'app/components/PatternUnstructuredList/VHSInlineDisplay';
import preventDefault from 'app/utils/preventDefault';

const PATTERNS_TO_DISPLAY = [...Object.keys(THORAX_PATTERNS), ...Object.keys(ABDOMEN_PATTERNS)];

interface StudyRowProps {
  study: Study;
  thresholdForDisplay: number;
  intl: IntlShape;
  selected: boolean;
  multiSelected: boolean;
  onMultiSelectChange: (checked: boolean) => void;
  onRowClick: (studyID: string) => void;
  onStudyRemove: (studyID: string) => void;
}

const StudyRow: React.FC<StudyRowProps> = ({
  study,
  thresholdForDisplay,
  intl,
  selected,
  multiSelected,
  onMultiSelectChange,
  onRowClick,
  onStudyRemove,
}) => {
  const predictionsWrapper = {
    predictions: _.mergeWith(
      {},
      ...study.images.map(({ predictions }) => predictions),
      predictionsMerger
    ),
    feedback: _.merge({}, ...study.images.map(({ feedback }) => feedback)),
  };

  const openStudyInViewer = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    browserHistory.push(`/viewer?study_ids[]=${study._id}`);
  };

  const objectDate = new Date(study.creationDate);
  const today = new Date();
  const sex = study?.patient?.sex;

  const handleSelectRow = () => {
    onRowClick(study._id);
  };

  const handleSelectStudyForRemoving = (event: React.MouseEvent) => {
    event.stopPropagation();
    onStudyRemove(study._id);
  };

  return (
    <Table.Row
      key={study._id}
      onClick={handleSelectRow}
      onDoubleClick={openStudyInViewer}
      id={study._id}
      className={selected ? 'selected' : ''}
    >
      <Table.Cell onClick={preventDefault} onDoubleClick={preventDefault}>
        <div>
          <Checkbox
            checked={multiSelected}
            aria-label={intl.formatMessage({ id: 'monitoring.study.select_for_comparison' })}
            onChange={(e, { checked }) => onMultiSelectChange(checked)}
          />
        </div>
      </Table.Cell>
      <Table.Cell className="date">
        <DisplayDate
          date={study.creationDate}
          format={
            objectDate.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0) ? 'today' : 'shortTime'
          }
        />
      </Table.Cell>
      <Table.Cell>{study?.patient?.file_id}</Table.Cell>
      <Table.Cell>{study?.patient?.owner_name}</Table.Cell>
      <Table.Cell>{study?.patient?.name}</Table.Cell>
      <Table.Cell>
        <AgeFromDate birthDate={study?.patient?.birth_date} referenceDate={objectDate} />
      </Table.Cell>
      <Table.Cell>
        {sex && sex !== 'other' ? (
          <i
            className={`svg-icon ${sex}`}
            title={intl.formatMessage({ id: `patient_info.sex.${sex}` })}
            aria-label={intl.formatMessage({ id: `patient_info.sex.${sex}` })}
          />
        ) : null}
      </Table.Cell>
      <Table.Cell>{study.images.length}</Table.Cell>

      <Table.Cell>
        <PatternUnstructuredList
          image={predictionsWrapper}
          patternsToDisplay={PATTERNS_TO_DISPLAY}
          thresholdForDisplay={thresholdForDisplay}
          showFeedbackButtons={false}
          displayShowAllButton={true}
          additionalPrefixPatterns={
            <VHSInlineDisplay patient={study.patient} images={study.images} />
          }
          hideConfidence
        />
      </Table.Cell>
      <Table.Cell style={{ textAlign: 'center' }}>
        <div className="actions">
          <div className="actions__icon">
            <Icon
              name="trash alternate"
              className="icon-trash picoxia-fix"
              onClick={handleSelectStudyForRemoving}
              title={intl.formatMessage({ id: 'general.delete' })}
              aria-label={intl.formatMessage({ id: 'general.delete' })}
            />
          </div>

          <div className="actions__icon">
            <Icon
              link
              className="eye icon picoxia-fix"
              onClick={openStudyInViewer}
              title={intl.formatMessage({ id: 'general.see' })}
              aria-label={intl.formatMessage({ id: 'general.see' })}
            />
          </div>
          <div className="actions__icon">
            <PACSSyncStatus study={study} />
          </div>

          <div className="actions__icon">
            <TeleradiologyStudyShortStatus
              studyId={study._id}
              initialAnalysisStatus={study?.teleradiology_study?.analysis_status}
            />
          </div>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

export default StudyRow;
